import { z } from 'zod';
import { ALERT_CRITICALITIES } from './alerts';
import { INITIATIVE_STATUSES } from './initiatives';
import { OBJECTIVE_PERFORMANCE_SOURCES } from './performance';
import { TREND_DIRECTIONS } from './kpi-history';
import { TRAFFIC_LIGHTS } from './status';

/**
 * Dashboard por perspectiva (T-142, RF-35; DEC-056): objetivos com a performance ponderada, KPIs relacionados,
 * tendência, desvios, iniciativas e ações em atraso de uma perspectiva no período.
 */

/** Itens das listas longas (iniciativas, ações, desvios informados). */
export const PERSPECTIVE_LIST_LIMIT = 10;

export const perspectiveDashboardQuerySchema = z
  .object({
    perspectiveId: z.string().uuid('Perspectiva inválida.').optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
  })
  .refine((v) => (v.year === undefined) === (v.month === undefined), {
    message: 'Informe o ano e o mês juntos.',
    path: ['month'],
  });

const light = z.enum(TRAFFIC_LIGHTS);
const list = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ total: z.number().int(), items: z.array(item) }).nullable();

export const perspectiveDashboardSchema = z.object({
  project: z.object({
    id: z.string().uuid(),
    name: z.string(),
    horizonStart: z.number().int(),
    horizonEnd: z.number().int(),
  }),
  year: z.number().int(),
  month: z.number().int(),
  computedAt: z.string().nullable(),
  /** As perspectivas do projeto (seletor), na ordem do mapa. */
  perspectives: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  /** A perspectiva escolhida (null: projeto sem perspectivas). */
  perspective: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  /** Performance ponderada no mês, acumulada e a tendência (p.p.) para o mês anterior. */
  performance: z
    .object({
      performance: z.number().nullable(),
      light,
      ytdPerformance: z.number().nullable(),
      trend: z.number().nullable(),
    })
    .nullable(),
  /** Objetivos com o peso efetivo na ponderação (null = sem acesso a Objetivos). */
  objectives: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        weight: z.number(),
        performance: z.number().nullable(),
        light,
        trend: z.number().nullable(),
        owner: z.string().nullable(),
        /** De onde vem a performance (DEC-032); a consolidada dos desdobramentos não entra na média da perspectiva. */
        source: z.enum(OBJECTIVE_PERFORMANCE_SOURCES).nullable(),
      }),
    )
    .nullable(),
  /** KPIs da perspectiva no mês (null = sem acesso a Indicadores). */
  kpis: z
    .array(
      z.object({
        id: z.string().uuid(),
        code: z.string(),
        name: z.string(),
        unitLabel: z.string(),
        actual: z.number().nullable(),
        target: z.number().nullable(),
        performance: z.number().nullable(),
        light,
      }),
    )
    .nullable(),
  /** Os 12 meses do ano da perspectiva e a direção nos últimos meses com dado. */
  trend: z.object({
    months: z.array(
      z.object({
        month: z.number().int(),
        performance: z.number().nullable(),
        light,
        ytdPerformance: z.number().nullable(),
      }),
    ),
    direction: z
      .object({
        direction: z.enum(TREND_DIRECTIONS),
        months: z.number().int(),
        first: z.object({ year: z.number().int(), month: z.number().int(), performance: z.number() }),
        last: z.object({ year: z.number().int(), month: z.number().int(), performance: z.number() }),
      })
      .nullable(),
  }),
  /** KPIs amarelos ou vermelhos no mês, com a diferença realizado − meta. */
  deviations: z
    .array(
      z.object({
        kpiId: z.string().uuid(),
        code: z.string(),
        name: z.string(),
        unitLabel: z.string(),
        actual: z.number().nullable(),
        target: z.number().nullable(),
        gap: z.number().nullable(),
        performance: z.number().nullable(),
        light,
      }),
    )
    .nullable(),
  /** Desvios informados abertos da perspectiva (alertas; null = sem acesso a Alertas). */
  reportedDeviations: list(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      message: z.string(),
      criticality: z.enum(ALERT_CRITICALITIES),
      createdAt: z.string(),
    }),
  ),
  initiatives: list(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      objective: z.string(),
      status: z.enum(INITIATIVE_STATUSES),
      progress: z.number(),
      endDate: z.string().nullable(),
      late: z.boolean(),
    }),
  ),
  lateActions: list(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      dueDate: z.string(),
      planId: z.string().uuid(),
      planName: z.string(),
      responsibleName: z.string().nullable(),
    }),
  ),
});
export type PerspectiveDashboard = z.infer<typeof perspectiveDashboardSchema>;
